import { useEffect, useRef, useCallback } from "react";
import { App } from "@capacitor/app";
import * as Sentry from "../lib/errorReporting";
import { supabase } from "../lib/supabase";
import { useRealtimeChannel } from "./useRealtimeChannel";
import { SUPPORTED_LEAGUES, ESPN_PRIMARY_LEAGUES } from "../lib/constants";

/**
 * Compute the 3-day window (yesterday, today, tomorrow) in YYYY-MM-DD local time.
 */
function getDateWindow() {
  const d = new Date();
  const fmt = (dt) => dt.toLocaleDateString("en-CA"); // YYYY-MM-DD
  const yesterday = new Date(d);
  yesterday.setDate(d.getDate() - 1);
  const tomorrow = new Date(d);
  tomorrow.setDate(d.getDate() + 1);
  return [fmt(yesterday), fmt(d), fmt(tomorrow)];
}

// A seed query still pending after this long is aborted and counts as a failure (#1547 N6).
const SEED_TIMEOUT_MS = 15000;
// The foreground reseed is skipped when any seed started less than this long ago (#1547 G7a).
const FOREGROUND_THROTTLE_MS = 15000;
const SENTRY_TAGS = { function: "useGameCacheRealtime" };

// Local date of the last zero-games capture: at most one per process per local day (#1547 G7c).
let zeroGamesReportedDay = null;

/** Reset module-level reporting state between tests; test-only. */
export function resetSeedReportingForTests() {
  zeroGamesReportedDay = null;
}

/**
 * Read an updated_at value as epoch ms; a missing or malformed value reads as 0, never NaN (#1547 N1).
 */
function tsOf(value) {
  const t = Date.parse(value);
  return Number.isFinite(t) ? t : 0;
}

/**
 * Whether a game lies inside a seed's prune scope: a supported league and one of the seed's dates.
 */
function inSeedScope(game, dateSet) {
  return SUPPORTED_LEAGUES.includes(game.league) && dateSet.has(game.gameDate);
}

/**
 * Transform a game_cache row's raw_data into the game shape used by the client.
 * Applies startTimeUTC → local startTime conversion.
 */
export function transformRow(row) {
  const g = row.raw_data;
  if (!g) return null;
  return {
    ...g,
    // Explicit DB columns are authoritative — cron writes them; raw_data can be stale.
    // `league` mirrors cacheRowToGame (settlement-processor.js): column first — it is a
    // league-authority signal on both sides (#1037 round 3 P1).
    league: row.league ?? g.league ?? null,
    status: row.status ?? g.status,
    home_score: row.home_score ?? g.home_score,
    away_score: row.away_score ?? g.away_score,
    home_team: row.home_team ?? g.home_team,
    away_team: row.away_team ?? g.away_team,
    // Nested objects consumed by calcStatus and ScoreboardWidget
    home: row.home_score != null
      ? { ...(g.home ?? {}), score: row.home_score, team: row.home_team ?? g.home?.team }
      : (g.home ?? {}),
    away: row.away_score != null
      ? { ...(g.away ?? {}), score: row.away_score, team: row.away_team ?? g.away?.team }
      : (g.away ?? {}),
    _updatedAt: row.updated_at || null,
    startTime: g.startTimeUTC
      ? new Date(g.startTimeUTC).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
      : (g.startTime || ""),
  };
}

/**
 * Derive dataSource from game source fields.
 * Priority: "espn" (fallback) > single healthy source > "live" (mixed).
 */
function deriveDataSource(games) {
  // Only leagues where ESPN is the FALLBACK carry a degradation signal. A league
  // whose primary IS ESPN always reports "espn" and would pin this permanently --
  // see ESPN_PRIMARY_LEAGUES. An UNKNOWN league is kept: a missing league is a data
  // gap, and dropping it would silently shrink a health signal.
  const signalling = games.filter((g) => !ESPN_PRIMARY_LEAGUES.includes(g.league));
  const sources = new Set(signalling.map((g) => g.source));
  if (sources.has("espn")) return "espn";
  if (sources.size === 1) return [...sources][0];
  return sources.size > 1 ? "live" : "unknown";
}

/**
 * Group an array of games into a map keyed by gameDate.
 */
function groupByDate(games) {
  const byDate = {};
  for (const g of games) {
    const date = g.gameDate || "unknown";
    if (!byDate[date]) byDate[date] = [];
    byDate[date].push(g);
  }
  return byDate;
}

/**
 * Subscribe to Supabase game_cache table via Realtime and seed initial state.
 *
 * @param {Object} params
 * @param {Function} params.setGames - setter for games array
 * @param {Function} params.setByDate - setter for byDate map
 * @param {Function} params.setDataSource - setter for data source string
 * @param {Function} params.setLastUpdated - setter for last-updated Date
 * @returns {{ refresh: Function }}
 */
export function useGameCacheRealtime({ setGames, setByDate, setDataSource, setLastUpdated }) {
  const gamesRef = useRef([]);
  // #1547 seed state. A seed commits only if it is newer than the last commit (G1).
  // Each in-flight seed owns a verdict map, created synchronously at its start:
  // id -> true (present) / false (gone), written by every Realtime event delivered
  // while it is in flight; the latest event for an id wins.
  const seedSeqRef = useRef(0);
  const lastCommittedSeqRef = useRef(0);
  const lastSeedStartedAtRef = useRef(null);
  const verdictsRef = useRef(new Map());
  // One warning per failure / prune-skipped streak (N7).
  const failureWarnedRef = useRef(false);
  const pruneSkipWarnedRef = useRef(false);
  // Whether any seed has published; a commit defect before that shows "unavailable".
  const publishedRef = useRef(false);

  const publish = useCallback(
    (games) => {
      gamesRef.current = games;
      setGames(games);
      setByDate(groupByDate(games));
      setDataSource(deriveDataSource(games));
      setLastUpdated(new Date());
      publishedRef.current = true;
    },
    [setGames, setByDate, setDataSource, setLastUpdated]
  );

  // A failed seed: "unavailable" only if no newer seed has committed (G1), and one
  // warning per failure streak (N7). Transient -- the next trigger reseeds (CC-IMPL-037).
  const failSeed = useCallback(
    (my, error) => {
      // A seed that can no longer commit changes nothing: the screen already holds newer
      // data, and it must not consume the warning of a real outage that follows.
      if (my <= lastCommittedSeqRef.current) return;
      setDataSource("unavailable");
      if (failureWarnedRef.current) return;
      failureWarnedRef.current = true;
      Sentry.captureMessage(`game_cache seed failed: ${error?.message || "non-array data"}`, {
        level: "warning",
        tags: { ...SENTRY_TAGS, stage: "seed" },
        extra: { code: error?.code ?? null },
      });
    },
    [setDataSource]
  );

  const seed = useCallback(async () => {
    if (!supabase) return;
    const my = ++seedSeqRef.current;
    const verdicts = new Map();
    verdictsRef.current.set(my, verdicts);
    lastSeedStartedAtRef.current = Date.now();
    const dates = getDateWindow(); // this seed's prune scope (G5)

    const controller = typeof AbortController === "function" ? new AbortController() : null;
    const timer = controller ? setTimeout(() => controller.abort(), SEED_TIMEOUT_MS) : null;
    let result;
    try {
      result = await supabase
        .from("game_cache")
        .select("*", { count: "exact" })
        .in("league", SUPPORTED_LEAGUES)
        .in("game_date", dates)
        .abortSignal(controller ? controller.signal : undefined);
    } catch (err) {
      failSeed(my, err); // reports a warning (once per streak)
      return;
    } finally {
      if (timer) clearTimeout(timer);
      verdictsRef.current.delete(my);
    }

    try {
      const { data, error, count } = result || {};
      // G4: postgrest-js returns {data: null, error: null} on an empty 2xx -- a failure, never a prune.
      if (error || !Array.isArray(data)) {
        failSeed(my, error);
        return;
      }
      if (my <= lastCommittedSeqRef.current) return; // a newer seed already committed

      for (const seq of [...verdictsRef.current.keys()]) {
        if (seq < my) verdictsRef.current.delete(seq); // older seeds can no longer commit
      }
      lastCommittedSeqRef.current = my;
      failureWarnedRef.current = false;

      const scope = new Set(dates);
      const memory = gamesRef.current;
      // Prune only on a provably complete response (N5) that is not an empty page over
      // shown in-scope games (N4).
      const complete = typeof count === "number" && data.length === count;
      // Rows without raw_data cannot render; an all-unreadable page counts as empty (Nova P2-3).
      const seedGames = data.map(transformRow).filter(Boolean);
      const emptyOverMemory = seedGames.length === 0 && memory.some((g) => inSeedScope(g, scope));
      const prune = complete && !emptyOverMemory;
      if (prune) {
        pruneSkipWarnedRef.current = false;
      } else if (!pruneSkipWarnedRef.current) {
        pruneSkipWarnedRef.current = true;
        Sentry.captureMessage("game_cache seed prune skipped: incomplete or empty response", {
          level: "warning",
          tags: { ...SENTRY_TAGS, stage: "seed-prune" },
          extra: { returned: data.length, count: count ?? null, inScopeShown: memory.filter((g) => inSeedScope(g, scope)).length },
        });
      }

      const merged = new Map(memory.map((g) => [String(g.id), g]));
      // Every returned row's id counts as returned, readable or not, so it is never pruned.
      const returned = new Set(data.map((row) => String(row?.raw_data?.id ?? row?.id)));
      for (const g of seedGames) {
        const key = String(g.id);
        if (verdicts.get(key) === false) continue; // gone since this seed started (unconditional, N1)
        const mem = merged.get(key);
        // Seed wins ties; memory wins only when strictly newer (an event delivered mid-seed).
        if (!mem || tsOf(g._updatedAt) >= tsOf(mem._updatedAt)) merged.set(key, g);
      }
      if (prune) {
        for (const g of memory) {
          const key = String(g.id);
          // An event delivered since this seed started outranks its snapshot (C1).
          if (!returned.has(key) && verdicts.get(key) !== true && inSeedScope(g, scope)) merged.delete(key);
        }
      }
      // The handler's rule: the current window, so a new-day row delivered after midnight survives.
      const window = new Set(getDateWindow());
      const games = Array.from(merged.values()).filter((g) => window.has(g.gameDate));
      if (!games.length) {
        const day = new Date().toLocaleDateString("en-CA");
        if (zeroGamesReportedDay !== day) {
          zeroGamesReportedDay = day;
          Sentry.captureException(new Error("game_cache seed returned zero games"), {
            tags: { ...SENTRY_TAGS, stage: "seed" },
          });
        }
      }
      publish(games);
    } catch (err) {
      // Not a transient query failure: the commit itself threw (a code defect) -- terminal.
      Sentry.captureException(err, { tags: { ...SENTRY_TAGS, stage: "seed-commit" } });
      if (!publishedRef.current) setDataSource("unavailable"); // never leave a first load on "loading"
    }
  }, [failSeed, publish, setDataSource]);

  // buildChannel: attaches postgres_changes handler. Must be stable (useCallback).
  const buildChannel = useCallback(
    (channel) =>
      channel.on(
        "postgres_changes",
        { event: "*", schema: "public", table: "game_cache", filter: `league=in.(${SUPPORTED_LEAGUES.join(',')})` },
        (payload) => {
          try {
            const { eventType } = payload;
            // One window per event, shared by the verdict and the state update (#1547 delta Q2).
            const dates = new Set(getDateWindow());
            let games = [...gamesRef.current];

            // #1547: record this event's verdict in every in-flight seed's map before
            // applying it. Gone verdicts are unconditional (N1): updated_at is stamped
            // before commit, so it cannot order a removal against a snapshot.
            const recordVerdict = (id, present) => {
              const key = String(id);
              for (const verdicts of verdictsRef.current.values()) verdicts.set(key, present);
            };

            if (eventType === "DELETE") {
              // DELETE carries only the replica-identity PK (residual R6).
              const oldId = payload.old?.id;
              recordVerdict(oldId, false);
              games = games.filter((g) => String(g.id) !== String(oldId));
            } else {
              // INSERT or UPDATE
              const row = payload.new;
              const inWindow = Boolean(row.game_date && dates.has(row.game_date));
              // Key by the id in-memory games carry: transformRow(row).id = raw_data.id (Adj-2).
              recordVerdict(transformRow(row)?.id ?? row.id, inWindow);
              if (inWindow) {
                // In-window — merge into state
                const transformed = transformRow(row);
                if (transformed) {
                  const idx = games.findIndex((g) => String(g.id) === String(transformed.id));
                  if (idx >= 0) {
                    games[idx] = transformed;
                  } else {
                    games.push(transformed);
                  }
                }
              } else {
                // Out-of-window — remove any existing entry with this id
                // (handles postponements / game_date corrections)
                const rowId = String(row.id);
                games = games.filter((g) => String(g.id) !== rowId);
              }
            }

            // Prune games whose gameDate fell out of the window (day rollover)
            games = games.filter((g) => dates.has(g.gameDate));
            gamesRef.current = games;
            setGames(games);
            setByDate(groupByDate(games));
            setDataSource(deriveDataSource(games));
            setLastUpdated(new Date());
          } catch (err) {
            Sentry.captureException(err, {
              tags: { function: "useGameCacheRealtime", stage: "realtime-event" },
            });
          }
        }
      ),
    [setGames, setByDate, setDataSource, setLastUpdated]
  );

  // Re-seed on recovery to backfill any game updates missed during the outage.
  // Kept alongside the rejoin reseed: while Realtime cannot rejoin but REST works,
  // it is the only automatic refresh.
  const onRecover = useCallback(() => {
    seed();
  }, [seed]);

  // Reseed after every non-first SUBSCRIBED (#1547 Adj-1, the useWireRealtime
  // pattern): onRecover fires before the resubscribe, so its snapshot can miss the
  // disconnected gap. The first SUBSCRIBED is skipped -- the mount effect seeds.
  const seededOnceRef = useRef(false);
  const onSubscribed = useCallback(() => {
    if (!seededOnceRef.current) {
      seededOnceRef.current = true;
      return;
    }
    seed();
  }, [seed]);

  useRealtimeChannel({
    enabled: true,
    channelName: "game-cache",
    sentryContext: { function: "useGameCacheRealtime", channel: "game-cache" },
    buildChannel,
    onRecover,
    onSubscribed,
  });

  // Foreground reseed (#1547): the app may have missed server deletes while in the
  // background (no DELETE events since #1542). Skipped if a seed started under 15 s
  // ago; a negative elapsed time (clock moved back) is not throttled.
  useEffect(() => {
    if (!supabase) return undefined;
    let handle = null;
    let cancelled = false;
    (async () => {
      try {
        const h = await App.addListener("appStateChange", ({ isActive }) => {
          if (!isActive) return;
          const startedAt = lastSeedStartedAtRef.current;
          const elapsed = startedAt == null ? Infinity : Date.now() - startedAt;
          if (elapsed >= 0 && elapsed < FOREGROUND_THROTTLE_MS) return;
          seed();
        });
        if (cancelled) {
          h.remove();
          return;
        }
        handle = h;
      } catch {
        // Degraded, not terminal: the other reseed triggers still run (CC-IMPL-037).
        Sentry.captureMessage("useGameCacheRealtime: appStateChange listener unavailable", {
          level: "warning",
          tags: { ...SENTRY_TAGS, stage: "foreground-listener" },
        });
      }
    })();
    return () => {
      cancelled = true;
      if (handle) handle.remove();
    };
  }, [seed]);

  // Initial seed + midnight reseed timer (separate effect — no channel dependency).
  useEffect(() => {
    if (!supabase) return;

    seed();

    // Re-seed at local midnight so the date window shifts cleanly.
    // setTimeout fires once at the next midnight, then setInterval every 24h.
    const now = new Date();
    const midnight = new Date(now);
    midnight.setHours(24, 0, 0, 0);
    const msUntilMidnight = midnight.getTime() - now.getTime();
    const DAY_MS = 24 * 60 * 60 * 1000;
    let dailyInterval = null;
    const midnightTimeout = setTimeout(() => {
      seed();
      dailyInterval = setInterval(seed, DAY_MS);
    }, msUntilMidnight);

    return () => {
      clearTimeout(midnightTimeout);
      if (dailyInterval) clearInterval(dailyInterval);
    };
  }, [seed]);

  return { refresh: seed };
}
